import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IconGift, IconChevronRight, IconCopy, IconCheck, IconX, IconDiscount, IconSparkles } from '@tabler/icons-react';
import { thunkFetchAvailableCouponsForCustomBasedOnOrderAmount } from '../../../redux/slices/couponSlice';

const CheckoutOrderCouponsWidget = ({ orderTotal, onCouponApply }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [hasCheckedCoupons, setHasCheckedCoupons] = useState(false);
  const [lastCheckedAmount, setLastCheckedAmount] = useState(null);

  const { 
    availableCouponsForOrder, 
    clientLoading, 
    clientError,
    bestAvailableDiscount 
  } = useSelector((state) => state.coupon);

  // Fetch coupons when order total changes
  useEffect(() => {
    if (orderTotal && orderTotal > 0 && lastCheckedAmount !== orderTotal) {
      setHasCheckedCoupons(false);
      dispatch(thunkFetchAvailableCouponsForCustomBasedOnOrderAmount(orderTotal))
        .finally(() => {
          setHasCheckedCoupons(true);
          setLastCheckedAmount(orderTotal);
        });
    }
  }, [dispatch, orderTotal, lastCheckedAmount]);

  // Auto-expand when new coupons are available
  useEffect(() => {
    if (availableCouponsForOrder && availableCouponsForOrder.length > 0 && !isExpanded) {
      // Auto-expand for 3 seconds when new coupons are found
      setIsExpanded(true);
      const timer = setTimeout(() => {
        setIsExpanded(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [availableCouponsForOrder]);

  // Don't render if no order total or still loading initial check
  if (!orderTotal || orderTotal <= 0 || (!hasCheckedCoupons && clientLoading)) {
    return null;
  }

  // Don't render if no coupons available
  if (!availableCouponsForOrder || availableCouponsForOrder.length === 0) {
    return null;
  }

  const handleCopyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      
      // Call the parent callback to apply the coupon
      if (onCouponApply) {
        onCouponApply(code);
      }
      
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.coupon_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else if (coupon.coupon_type === 'fixed_amount') {
      return `$${coupon.discount_value} OFF`;
    } else if (coupon.coupon_type === 'free_shipping') {
      return 'FREE SHIPPING';
    }
    return 'SPECIAL OFFER';
  };

  const formatExpiry = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-40 ">
      {/* Collapsed State - Sticky Tab */}
      {!isExpanded && (
        <div
          onClick={() => setIsExpanded(true)}
          className="bg-gradient-to-l from-[#FFD500] to-[#FFA500] text-[#0B0C52] 
                     px-4 py-4 rounded-l-lg cursor-pointer shadow-lg hover:shadow-xl 
                     transition-all duration-300 hover:scale-105 group relative"
        >
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center">
              <IconSparkles size={20} className="animate-pulse" />
              <div className="text-xs font-bold mt-1">
                SAVE ${bestAvailableDiscount?.toFixed(0) || '0'}
              </div>
            </div>
            <div className="writing-mode-vertical-lr text-sm font-bold">
              {availableCouponsForOrder.length} COUPON{availableCouponsForOrder.length > 1 ? 'S' : ''}
            </div>
            <IconChevronRight size={16} className="group-hover:-translate-x-1 transition-transform rotate-180" />
          </div>
          
          {/* Notification Badge */}
          <div className="absolute -top-2 -left-2 bg-red-500 text-white text-xs 
                          rounded-full w-6 h-6 flex items-center justify-center font-bold">
            {availableCouponsForOrder.length}
          </div>

          {/* Savings Badge */}
          {bestAvailableDiscount > 0 && (
            <div className="absolute -bottom-2 -left-2 bg-green-500 text-white text-xs 
                            rounded-full px-2 py-1 font-bold">
              Save ${bestAvailableDiscount.toFixed(0)}
            </div>
          )}
        </div>
      )}

      {/* Expanded State - Coupon Panel */}
      {isExpanded && (
        <div className="bg-white rounded-l-lg shadow-2xl border-r-4 border-[#FFD500] 
                        w-80 max-h-100 overflow-y-auto">
          {/* Header */}
          <div className="bg-gradient-to-l from-[#0B0C52] to-[#002080] text-white p-4 rounded-tl-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IconDiscount size={20} />
                <div>
                  <h3 className="font-bold text-sm">Available Savings</h3>
                  <p className="text-xs opacity-90">For your ${orderTotal?.toFixed(2)} order</p>
                </div>
              </div>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <IconX size={16} />
              </button>
            </div>
          </div>

          {/* Best Savings Highlight */}
          {bestAvailableDiscount > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 border-b">
              <div className="text-center">
                <div className="text-lg font-bold text-green-700">
                  💰 Save up to ${bestAvailableDiscount.toFixed(2)}
                </div>
                <div className="text-xs text-green-600">
                  Apply the best coupon below
                </div>
              </div>
            </div>
          )}

          {/* Coupons List */}
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {availableCouponsForOrder.map((coupon, index) => (
              <div 
                key={coupon.id}
                className="border border-dashed border-[#FFD500] rounded-lg p-3 
                           bg-gradient-to-r from-[#FFD500]/10 to-[#FFA500]/10 relative
                           hover:from-[#FFD500]/20 hover:to-[#FFA500]/20 transition-all"
              >
                {/* Best Deal Badge */}
                {index === 0 && (
                  <div className="absolute -top-2 -right-2 bg-[#0B0C52] text-white 
                                  text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <IconSparkles size={10} />
                    BEST
                  </div>
                )}

                <div className="space-y-2">
                  {/* Discount Info */}
                  <div className="flex items-center justify-between">
                    <div className="font-bold text-[#0B0C52] text-lg">
                      {formatDiscount(coupon)}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-bold text-green-600">
                        Save ${coupon.potential_discount?.toFixed(2)}
                      </div>
                      <div className="text-xs text-green-500">
                        {coupon.savings_percentage}% off
                      </div>
                    </div>
                  </div>

                  {/* Coupon Name */}
                  <div className="text-sm text-gray-600 font-medium">
                    {coupon.name}
                  </div>

                  {/* Code and Copy Button */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono font-bold text-[#0B0C52] text-sm 
                                    bg-white border-2 border-dashed border-[#0B0C52] 
                                    px-2 py-1 rounded flex-1 text-center">
                      {coupon.code}
                    </div>
                    <button
                      onClick={() => handleCopyCode(coupon.code)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        copiedCode === coupon.code
                          ? 'bg-green-100 text-green-700'
                          : 'bg-[#0B0C52] text-white hover:bg-[#002080]'
                      }`}
                    >
                      {copiedCode === coupon.code ? (
                        <>
                          <IconCheck size={12} />
                          <span className="ml-1">Applied!</span>
                        </>
                      ) : (
                        <>
                          <IconCopy size={12} />
                          <span className="ml-1">Apply</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Additional Info */}
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Expires {formatExpiry(coupon.end_date)}</span>
                    {coupon.max_uses && (
                      <span>{coupon.current_uses || 0}/{coupon.max_uses} used</span>
                    )}
                  </div>

                  {/* Minimum Order Info */}
                  {coupon.minimum_order_amount && parseFloat(coupon.minimum_order_amount) > 0 && (
                    <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                      ✓ Minimum order: ${coupon.minimum_order_amount} (qualified)
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="border-t p-3 bg-gray-50 rounded-bl-lg">
            <p className="text-xs text-gray-600 text-center">
              🎉 Coupons automatically applied at checkout!
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutOrderCouponsWidget;